import React, { useEffect, useMemo, useRef } from 'react';
import { Chart } from 'chart.js/auto';

export default function GenderAnalytics({ data = {}, loading = false }) {
  const doughnutRef = useRef(null);
  const lineRef = useRef(null);
  const ageRef = useRef(null);

  // Normalized lists
  const genders = useMemo(() => {
    const normalizeGender = (g) => {
      if (g === null || g === undefined) return 'Tidak diketahui';
      if (typeof g === 'string' && g.trim() === '') return ''; // kategori kosong
      return g.trim();
    };

    const map = {};

    (data.countUsersByGender || []).forEach((r) => {
      const key = normalizeGender(r.gender);

      map[key] = {
        gender: key,
        total_users: Number(r.total_users || 0),
        percentage: Number(r.percentage || 0),
      };
    });

    // Urutan yang kamu pakai
    const order = ['Perempuan', 'Laki Laki', 'Tidak diketahui', ''];

    // susun berdasarkan order, sisanya append
    const ordered = order
      .map((k) => map[k])
      .filter(Boolean)
      .concat(Object.values(map).filter((v) => !order.includes(v.gender)));

    return ordered;
  }, [data.countUsersByGender]);

  const totalUsers = useMemo(
    () => genders.reduce((s, g) => s + (g.total_users || 0), 0),
    [genders]
  );

  // PREPARE line chart data: months sorted asc, two series per gender found
  const monthly = useMemo(() => {
    const rows = data.countUsersByGenderOverTime || [];
    // collect months
    const monthsSet = new Set();
    rows.forEach((r) => monthsSet.add(r.month));
    const months = Array.from(monthsSet)
      .filter(Boolean)
      .sort((a, b) => (a > b ? 1 : -1));

    // find genders present
    const genderSet = new Set();
    rows.forEach((r) => genderSet.add(r.sex || 'Tidak diketahui'));
    const genderList = Array.from(genderSet);

    // build map: month -> gender -> total
    const map = {};
    months.forEach((m) => (map[m] = {}));
    rows.forEach((r) => {
      const m = r.month;
      if (!m) return; // baris tanpa bulan valid sudah dibuang dari `months`, jangan ikut ditulis
      const g = r.sex || 'Tidak diketahui';
      map[m][g] = (map[m][g] || 0) + Number(r.total || 0);
    });

    // dataset per gender
    const datasets = genderList.map((g, idx) => {
      // color palette (repeatable)
      const palette = ['#0d6efd', '#198754', '#ffc107', '#6c757d', '#fd7e14'];
      return {
        label: g,
        data: months.map((m) => map[m][g] || 0),
        tension: 0.3,
        borderColor: palette[idx % palette.length],
        backgroundColor: palette[idx % palette.length],
        fill: false,
        pointRadius: 3,
      };
    });

    return { months, genderList, datasets };
  }, [data.countUsersByGenderOverTime]);

  // PREPARE age chart: bucket by age and gender (show grouped bars)
  const ageData = useMemo(() => {
    const rows = data.countUsersByAge || [];
    // build map age -> gender -> total
    const ageSet = new Set();
    const gendersSet = new Set();
    rows.forEach((r) => {
      const a = Number(r.age ?? 0);
      const g = r.sex || 'Tidak diketahui';
      ageSet.add(a);
      gendersSet.add(g);
    });
    const ages = Array.from(ageSet).sort((a, b) => a - b);
    const gendersArr = Array.from(gendersSet);
    const map = {};
    ages.forEach((a) => (map[a] = {}));
    rows.forEach((r) => {
      const a = Number(r.age ?? 0);
      if (!map[a]) return;
      const g = r.sex || 'Tidak diketahui';
      map[a][g] = (map[a][g] || 0) + Number(r.total || 0);
    });

    const datasets = gendersArr.map((g, idx) => {
      const palette = ['#0d6efd', '#198754', '#ffc107', '#6c757d'];
      return {
        label: g,
        data: ages.map((a) => map[a][g] || 0),
        backgroundColor: palette[idx % palette.length],
      };
    });

    return { ages, gendersArr, datasets };
  }, [data.countUsersByAge]);

  // SIMPLE INSIGHTS COMPUTATION
  const topGender = genders[0] || null;
  // compute month-over-month growth for latest gender combined
  const latestMonth = monthly.months[monthly.months.length - 1];
  const prevMonth = monthly.months[monthly.months.length - 2];
  let growthCombined = 0;
  if (latestMonth && prevMonth) {
    const sumFor = (m) =>
      (data.countUsersByGenderOverTime || [])
        .filter((r) => r.month === m)
        .reduce((s, r) => s + (r.total || 0), 0);
    const latestSum = sumFor(latestMonth);
    const prevSum = sumFor(prevMonth);
    if (prevSum > 0)
      growthCombined = (((latestSum - prevSum) / prevSum) * 100).toFixed(1);
  }

  // Chart rendering and cleanup
  useEffect(() => {
    // DOUGHNUT: gender distribution
    const doughCtx = doughnutRef.current?.getContext('2d');
    let doughChart;
    if (!loading && doughCtx) {
      const labels = genders.map((g) => g.gender);
      const values = genders.map((g) => g.total_users);
      const colors = ['#0d6efd', '#198754', '#6c757d', '#ffc107', '#fd7e14'];
      doughChart = new Chart(doughCtx, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors.slice(0, labels.length),
              hoverOffset: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom' },
          },
        },
      });
    }
    return () => doughChart?.destroy();
  }, [loading, genders]);

  useEffect(() => {
    // LINE: monthly trend
    const ctx = lineRef.current?.getContext('2d');
    let lineChart;
    if (!loading && ctx && monthly.months && monthly.months.length > 0) {
      lineChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: monthly.months,
          datasets: monthly.datasets.map((d) => ({
            ...d,
            borderWidth: 2,
            backgroundColor: d.borderColor,
            tension: 0.25,
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: { legend: { position: 'bottom' } },
          scales: {
            x: { display: true },
            y: { beginAtZero: true },
          },
        },
      });
    }
    return () => lineChart?.destroy();
  }, [loading, monthly]);

  useEffect(() => {
    // AGE: grouped bar
    const ctx = ageRef.current?.getContext('2d');
    let ageChart;
    if (!loading && ctx && ageData.ages && ageData.ages.length > 0) {
      ageChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ageData.ages.map((a) => String(a)),
          datasets: ageData.datasets.map((d) => ({
            ...d,
            borderWidth: 0,
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom' } },
          scales: {
            x: { stacked: false },
            y: { beginAtZero: true },
          },
        },
      });
    }
    return () => ageChart?.destroy();
  }, [loading, ageData]);

  // Early return for empty data
  if (!loading && (!data.countUsersByGender || data.countUsersByGender.length === 0)) {
    return (
      <div className="text-muted small text-center py-4">
        <i className="bi bi-inbox fs-3 d-block mb-2 opacity-50"></i>
        Tidak ada data gender untuk ditampilkan.
      </div>
    );
  }

  // render
  return (
    <div>
      {/* SUMMARY CARDS */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="small text-muted">
              Total Pengguna (dengan gender)
            </div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">{totalUsers}</div>
                <div className="small text-muted">
                  {genders.length} kategori (termasuk kosong/tidak diketahui)
                </div>
              </>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="small text-muted">Dominant Gender</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">
                  {topGender
                    ? `${topGender.gender} (${topGender.total_users})`
                    : '-'}
                </div>
                <div className="small text-muted">
                  {topGender ? `${topGender.percentage}% dari total` : '—'}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm rounded-4 border-0 p-3 h-100">
            <div className="small text-muted">Growth (Latest MoM)</div>
            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" />
              </div>
            ) : (
              <>
                <div className="h4 fw-bold">
                  {growthCombined ? `${growthCombined}%` : '-'}
                </div>
                <div className="small text-muted">
                  Perbandingan {prevMonth || '-'} → {latestMonth || '-'}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MAIN VISUALS */}
      <div className="row mb-4">
        {/* Left: Doughnut + line */}
        <div className="col-lg-5 mb-3">
          <div className="card shadow-sm rounded-4 border-0 p-3 mb-3">
            <div className="card-body">
              <h6 className="fw-semibold mb-2">
                <i className="bi bi-pie-chart me-2 text-primary"></i>
                Proporsi Gender
              </h6>

              <div style={{ height: 260 }} className="mb-2">
                {loading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div className="spinner-border text-primary" />
                    <div className="small text-muted mt-2">Memuat data...</div>
                  </div>
                ) : (
                  <canvas ref={doughnutRef} />
                )}
              </div>

              <div className="small text-muted">
                {genders.map((g, i) => (
                  <div key={i} className="d-flex justify-content-between">
                    <div>{g.gender}</div>
                    <div>
                      {g.total_users} ({g.percentage}%)
                    </div>
                  </div>
                ))}
              </div>

              <p className="chart-caption">
                <i className="bi bi-lightbulb text-warning me-1"></i>
                Cara baca: makin besar irisan, makin dominan gender itu. Angka pastinya ada di daftar di atas.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Age distribution + insights */}
        <div className="col-lg-7">
          <div className="card shadow-sm rounded-4 border-0 p-3 mb-3">
            <div className="card-body">
              <h6 className="fw-semibold mb-2">
                <i className="bi bi-person-lines-fill me-2 text-warning"></i>
                Distribusi Usia (per Gender)
              </h6>
              <div style={{ height: 260 }}>
                {loading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div className="spinner-border text-warning" />
                    <div className="small text-muted mt-2">Memuat usia...</div>
                  </div>
                ) : ageData.ages && ageData.ages.length === 0 ? (
                  <div className="text-muted small text-center py-4">
                    Tidak ada data usia
                  </div>
                ) : (
                  <canvas ref={ageRef} />
                )}
              </div>
              {!loading && ageData.ages && ageData.ages.length > 0 && (
                <p className="chart-caption">
                  <i className="bi bi-lightbulb text-warning me-1"></i>
                  Cara baca: tiap batang = jumlah peserta di usia tersebut, dipisah per warna gender. Bandingkan tinggi batang antar warna untuk lihat kesenjangan usia.
                </p>
              )}
            </div>
          </div>

          <div className="card shadow-sm rounded-4 border-0 p-3 mb-3">
            <div className="card-body">
              <h6 className="fw-semibold mb-2">
                <i className="bi bi-graph-up me-2 text-success"></i>
                Tren Pendaftaran per Bulan
              </h6>
              <div style={{ height: 220 }}>
                {loading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div className="spinner-border text-success" />
                    <div className="small text-muted mt-2">Memuat tren...</div>
                  </div>
                ) : monthly.months.length === 0 ? (
                  <div className="text-muted small text-center py-4">
                    Tidak ada data tren
                  </div>
                ) : (
                  <canvas ref={lineRef} />
                )}
              </div>
              {!loading && monthly.months.length > 0 && (
                <p className="chart-caption">
                  <i className="bi bi-lightbulb text-warning me-1"></i>
                  Cara baca: garis naik berarti pendaftaran bertambah dari bulan sebelumnya, garis turun berarti berkurang. Bandingkan antar warna untuk lihat tren tiap gender.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .chart-caption { font-size: 0.75rem; color: #6c757d; margin: 10px 0 0; }
      `}</style>
    </div>
  );
}
